import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge
} from '@kroll-resolver-crisp/ui-components-react';
import {type Submission} from '@/api/submissions';
import {StatusIcon} from '@/components/shared/StatusIcon';
import {statusLabel, statusTone, toneBadge, worstStatus} from '@/lib/status';

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
}

function SubmissionRow({submission}: {readonly submission: Submission}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <span className="text-foreground font-medium">{submission.policy_violation}</span>
      {submission.risks.length > 0 ? (
        <>
          <span aria-hidden="true">&rsaquo;</span>
          <span className="text-foreground">{submission.risks.join(', ')}</span>
        </>
      ) : null}
      <span aria-hidden="true">&middot;</span>
      <span>{submission.created_by}</span>
      <span aria-hidden="true">&middot;</span>
      <span>{formatDate(submission.created_at)}</span>
      <Badge
        title={
          submission.delivered_at
            ? `Sent to client ${formatDate(submission.delivered_at)}`
            : undefined
        }
        {...toneBadge(statusTone(submission.action_status))}>
        {statusLabel(submission.action_status)}
      </Badge>
    </div>
  );
}

// Sits above an entity's card when it has prior submissions. The worst status (sent beats failed
// beats in-hopper) picks the icon, so an analyst sees "already sent" before they classify again.
export function ExistingSubmissionBadge({submissions}: {readonly submissions: Submission[]}) {
  const [first, ...rest] = submissions;
  if (!first) {
    return null;
  }

  const worst = worstStatus(submissions.map((s) => s.action_status));
  const tone = statusTone(worst);
  const sent = worst === 'sent to client';

  return (
    <Alert>
      <StatusIcon tone={tone} label={statusLabel(worst)} className="mt-0.5" />
      <AlertTitle>
        {sent ? 'Already sent to client' : 'Already submitted'}
        {submissions.length > 1 ? ` — ${submissions.length} prior submissions` : ''}
      </AlertTitle>
      <AlertDescription className="gap-0.5">
        <SubmissionRow submission={first} />
        {rest.map((s) => (
          <SubmissionRow key={s.id} submission={s} />
        ))}
      </AlertDescription>
    </Alert>
  );
}
